import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { PayloadService } from '../../../services/payload.service';

// 24 luglio 2026, ore 12:00 in Italia (CEST = UTC+2)
const RELEASE_DATE_UTC = Date.UTC(2026, 6, 24, 10, 0, 0);

const SONG_SLUG = 'oltre-i-miei-limiti';
const SONG_TITLE = 'Oltre i miei limiti';
const ARTIST_NAME = 'ComeTralci Worship';

// TODO: inserire i link quando disponibili (lasciare '' per nascondere il pulsante)
const PRESAVE_URL =
  'https://distrokid.com/hyperfollow/cometralciworshipfrancescolondinomicheladimattiagabrielechiocca/oltre-i-miei-limiti-feat-ohel-teen--overcomer-2?ref=release'; // link pre-save DistroKid
const YOUTUBE_URL = 'https://www.youtube.com/@ComeTralci_Official'; // canale YouTube del gruppo worship
const INSTAGRAM_URL = 'https://www.instagram.com/cometralci_official/'; // profilo Instagram

// TODO: caricare i file in frontend/public a questi percorsi
const COVER_URL = '/images/worship/oltre-i-miei-limiti-cover.jpg';
const PREVIEW_AUDIO_URL = '/media/worship/oltre-i-miei-limiti-preview.mp3';

@Component({
  selector: 'app-oltre-i-miei-limiti',
  imports: [CommonModule, FormsModule],
  templateUrl: './oltre-i-miei-limiti.component.html',
  styleUrl: './oltre-i-miei-limiti.component.scss',
})
export class OltreIMieiLimitiComponent implements OnInit, OnDestroy {
  private meta = inject(Meta);
  private title = inject(Title);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private payloadService = inject(PayloadService);

  @ViewChild('previewAudio') previewAudio?: ElementRef<HTMLAudioElement>;

  readonly songTitle = SONG_TITLE;
  readonly artistName = ARTIST_NAME;
  readonly presaveUrl = PRESAVE_URL;
  readonly youtubeUrl = YOUTUBE_URL;
  readonly instagramUrl = INSTAGRAM_URL;
  readonly coverUrl = COVER_URL;
  readonly previewAudioUrl = PREVIEW_AUDIO_URL;

  // Countdown
  released = false;
  countdown = { giorni: 0, ore: 0, minuti: 0, secondi: 0 };
  private countdownInterval?: ReturnType<typeof setInterval>;

  // Copertina
  coverError = false;

  // Player anteprima
  audioAvailable = true;
  isPlaying = false;
  audioProgress = 0;
  audioCurrentTime = '0:00';
  audioDuration = '0:00';

  // Form email
  email = '';
  submitting = false;
  subscribed = false;
  subscribeMessage = '';
  subscribeError = false;

  ngOnInit(): void {
    this.setSeoMeta();
    this.setJsonLd();
    this.updateCountdown();

    if (isPlatformBrowser(this.platformId)) {
      this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
    }
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  private updateCountdown(): void {
    const diff = RELEASE_DATE_UTC - Date.now();

    if (diff <= 0) {
      this.released = true;
      this.countdown = { giorni: 0, ore: 0, minuti: 0, secondi: 0 };
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      return;
    }

    this.countdown = {
      giorni: Math.floor(diff / (1000 * 60 * 60 * 24)),
      ore: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minuti: Math.floor((diff / (1000 * 60)) % 60),
      secondi: Math.floor((diff / 1000) % 60),
    };
  }

  pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  // --- Player anteprima ---

  togglePlay(): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio) return;

    if (this.isPlaying) {
      audio.pause();
      this.isPlaying = false;
    } else {
      audio.play().then(
        () => (this.isPlaying = true),
        () => (this.audioAvailable = false),
      );
    }
  }

  onAudioTimeUpdate(): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio || !audio.duration) return;
    this.audioProgress = (audio.currentTime / audio.duration) * 100;
    this.audioCurrentTime = this.formatTime(audio.currentTime);
    this.audioDuration = this.formatTime(audio.duration);
  }

  onAudioEnded(): void {
    this.isPlaying = false;
    this.audioProgress = 0;
    this.audioCurrentTime = '0:00';
    const audio = this.previewAudio?.nativeElement;
    if (audio) audio.currentTime = 0;
  }

  onAudioError(): void {
    this.audioAvailable = false;
    this.isPlaying = false;
  }

  seekAudio(event: MouseEvent): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio || !audio.duration) return;
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    audio.currentTime = Math.max(0, Math.min(1, ratio)) * audio.duration;
  }

  private formatTime(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  // --- Form email ---

  submitEmail(): void {
    const email = this.email.trim();
    if (this.submitting || this.subscribed) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.subscribeError = true;
      this.subscribeMessage = 'Inserisci un indirizzo email valido';
      return;
    }

    this.submitting = true;
    this.subscribeError = false;
    this.subscribeMessage = '';

    this.payloadService.subscribeWorshipRelease(email, SONG_SLUG).subscribe({
      next: () => {
        this.submitting = false;
        this.subscribed = true;
        this.subscribeMessage =
          'Fatto! Ti avviseremo appena la canzone sarà disponibile.';
      },
      error: (err) => {
        this.submitting = false;
        if (err?.status === 409) {
          this.subscribed = true;
          this.subscribeMessage = 'Questa email è già registrata, sei a posto!';
        } else {
          this.subscribeError = true;
          this.subscribeMessage =
            'Qualcosa è andato storto, riprova più tardi.';
        }
      },
    });
  }

  // --- SEO ---

  private setSeoMeta(): void {
    const pageTitle = `${SONG_TITLE} - ${ARTIST_NAME}`;
    const description = `"${SONG_TITLE}", il nuovo singolo di ${ARTIST_NAME}, disponibile su tutte le piattaforme digitali dal 24 luglio alle 12:00. Ascolta l'anteprima e ricevi una notifica all'uscita.`;
    const url = 'https://cometralci.com/worship/oltre-i-miei-limiti';

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });

    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'music.song' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({
      property: 'og:image',
      content: `https://cometralci.com${COVER_URL}`,
    });
    this.meta.updateTag({ property: 'og:site_name', content: 'Come Tralci' });
    this.meta.updateTag({ property: 'og:locale', content: 'it_IT' });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({
      name: 'twitter:image',
      content: `https://cometralci.com${COVER_URL}`,
    });

    this.updateCanonicalUrl(url);
  }

  private setJsonLd(): void {
    const existingScript = this.document.querySelector(
      'script#oltre-i-miei-limiti-jsonld',
    );
    if (existingScript) existingScript.remove();

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'MusicRecording',
      name: SONG_TITLE,
      byArtist: {
        '@type': 'MusicGroup',
        name: ARTIST_NAME,
      },
      datePublished: '2026-07-24',
      image: `https://cometralci.com${COVER_URL}`,
      url: 'https://cometralci.com/worship/oltre-i-miei-limiti',
    };

    const script = this.document.createElement('script');
    script.id = 'oltre-i-miei-limiti-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }

  private updateCanonicalUrl(url: string): void {
    let link = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement;
    if (link) {
      link.href = url;
    } else {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = url;
      this.document.head.appendChild(link);
    }
  }
}
